/*
	Sometimes you want to display an image, but if it fails to load, you want to display a fallback image to
	avoid a broken image icon.

	useImageFallback is a hook that will monitor the loading status of an image, and if it fails to load,
	it will display the fallback image instead.

	it receives the image url and the fallback url and returns an object with the following properties:
		- imageSrc: the current source of the image, which will be the fallback url if the image fails to load
		- isError: a boolean indicating if the image has failed to load
		- onError: a function that can be used to set the error state to true
		- resetError: a function that can be used to set the error state to false
*/

import { useState, useEffect } from "react";

export default function useImageFallback(imageUrl, fallbackUrl) {
	const [imageLoadError, setImageLoadError] = useState(false);

	useEffect(() => {
		setImageLoadError(false);

		if (imageUrl) {
			const img = new Image();
			img.onload = () => setImageLoadError(false);
			img.onerror = () => setImageLoadError(true);
			img.src = imageUrl;
		}
	}, [imageUrl]);

	const currentSrc = (!imageUrl || imageLoadError) ? fallbackUrl : imageUrl;

	const handleImageError = () => {
		setImageLoadError(true);
	};

	const resetError = () => {
		setImageLoadError(false);
	};

	return {
		imageSrc: currentSrc,
		isError: imageLoadError,
		onError: handleImageError,
		resetError,
	};
}
